import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useChartOfAccounts, ACCOUNT_TYPE_LABELS } from "@/hooks/useChartOfAccounts";
import type { ExpenseCategory } from "@/hooks/useExpenseCategories";

const NO_ACCOUNT_VALUE = "__none__";

interface ExpenseCategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: ExpenseCategory | null;
  onSubmit: (data: { name: string; chart_account_id: string | null; is_active?: boolean }) => Promise<void>;
}

export function ExpenseCategoryFormDialog({
  open,
  onOpenChange,
  category,
  onSubmit,
}: ExpenseCategoryFormDialogProps) {
  const { accounts, getLeafAccounts, initializeAccounts, isLoading: isLoadingAccounts } = useChartOfAccounts();
  const [name, setName] = useState("");
  const [chartAccountId, setChartAccountId] = useState<string>(NO_ACCOUNT_VALUE);
  const [isActive, setIsActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize chart of accounts if empty
  useEffect(() => {
    if (open && accounts.length === 0 && !isLoadingAccounts) {
      initializeAccounts();
    }
  }, [open, accounts.length, isLoadingAccounts, initializeAccounts]);

  useEffect(() => {
    if (open) {
      if (category) {
        setName(category.name);
        setChartAccountId(category.chart_account_id || NO_ACCOUNT_VALUE);
        setIsActive(category.is_active);
      } else {
        setName("");
        setChartAccountId(NO_ACCOUNT_VALUE);
        setIsActive(true);
      }
    }
  }, [open, category]);

  // Get expense-related accounts (fixed_expense, variable_cost, financial_expense)
  const expenseAccounts = [
    ...getLeafAccounts("fixed_expense"),
    ...getLeafAccounts("variable_cost"),
    ...getLeafAccounts("financial_expense"),
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSaving(true);
    try {
      await onSubmit({
        name: name.trim(),
        chart_account_id: chartAccountId === NO_ACCOUNT_VALUE ? null : chartAccountId,
        is_active: isActive,
      });
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {category ? "Editar Categoria de Despesa" : "Nova Categoria de Despesa"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Aluguel, Marketing, Manutenção"
            />
          </div>

          <div className="space-y-2">
            <Label>Conta do Plano de Contas</Label>
            <Select
              value={chartAccountId}
              onValueChange={setChartAccountId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma conta (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ACCOUNT_VALUE}>(Sem vínculo)</SelectItem>
                {expenseAccounts.map((acc) => (
                  <SelectItem key={acc.id} value={acc.id}>
                    {acc.code} - {acc.name} ({ACCOUNT_TYPE_LABELS[acc.account_type]})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Vincule a uma conta do plano para classificação automática no DRE
            </p>
          </div>

          {category && (
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label>Ativa</Label>
                <p className="text-sm text-muted-foreground">
                  Categoria disponível para uso
                </p>
              </div>
              <Switch
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving || !name.trim()}>
              {isSaving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
